import { Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { FormGroupStyling } from '../../entities/form-group-styling.entity';
import { FormSelectOption } from '../../entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../entities/multi-form-select-options.entity';
import { normalizeLocale } from '../../helpers/normalize-locale.helper';
import { ShFormGroupComponent } from '../_core/form-group/form-group.component';
import { LabelIcon } from '../_core/form-label/label-icon.entity';
import { ShFormMultiSelectBaseComponent } from '../form-multi-select/base-simple/form-multi-select-base.component';
import { ShFormNumberBaseComponent } from '../form-number/base-simple/form-number-base.component';

@Component({
    selector: 'sh-form-date-select',
    templateUrl: './form-date-select.component.html',
    styleUrls: ['./form-date-select.component.scss'],
    standalone: true,
    imports: [
        ShFormGroupComponent,
        ShFormNumberBaseComponent,
        ReactiveFormsModule,
        ShFormMultiSelectBaseComponent,
        AsyncPipe
    ]
})
export class ShFormDateSelectComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly localeId: string;

    public dayMultiFormSelectOptions$!: Observable<MultiFormSelectOptions>;
    public monthMultiFormSelectOptions!: MultiFormSelectOptions;

    @Input() public id!: string;
    @Input() public control!: UntypedFormGroup;

    @Input() public label?: string;
    @Input() public helpText?: string;

    @Input() public labelIcons: LabelIcon[] = [];

    @Input() public yearLabel = 'Year';
    @Input() public monthLabel = 'Month';
    @Input() public dayLabel = 'Day';

    @Input() public inOverflownContainer = false;
    @Input() public limitHeightToViewport = false;
    @Input() public formGroupStyling: FormGroupStyling[] = [];

    internalYearControl: UntypedFormControl = new UntypedFormControl();
    internalMonthControl: UntypedFormControl = new UntypedFormControl();
    internalDayControl: UntypedFormControl = new UntypedFormControl();

    constructor(@Inject(LOCALE_ID) localeId: string) {
        super();
        this.localeId = normalizeLocale(localeId);
    }

    ngOnInit(): void {
        this.initFormOptions();
        this.initFormListener();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initFormOptions(): void {
        const yearOrMonthChanges$: Observable<[number, number]> = combineLatest([
            this.yearControl.valueChanges.pipe(startWith(this.yearControl.value), takeUntil(this.destroy$)),
            this.monthControl.valueChanges.pipe(startWith(this.monthControl.value), takeUntil(this.destroy$))
        ]);

        yearOrMonthChanges$
            .pipe(takeUntil(this.destroy$))
            .subscribe(([year, month]) =>
                this.setDayControlToLastDayOfMonthIfNeeded(year, month, this.dayControl.value)
            );

        this.dayMultiFormSelectOptions$ = yearOrMonthChanges$.pipe(
            map(([year, month]) => this.getDayMultiFormSelectOptions(year, month)),
            takeUntil(this.destroy$)
        );

        this.monthMultiFormSelectOptions = {
            isLiveData: false,
            options: this.getMonthMultiFormSelectOptions()
        };
    }

    private setDayControlToLastDayOfMonthIfNeeded(year?: number, month?: number, day?: number): void {
        if (!year || !month || !day) {
            return;
        }

        const lastDayOfMonth: number = this.getLastDayOfMonth(year, month);

        if (lastDayOfMonth < day) {
            this.dayControl.setValue(lastDayOfMonth);
        }
    }

    private getDayMultiFormSelectOptions(year?: number, month?: number): MultiFormSelectOptions {
        const lastDayOfMonth: number = year && month ? this.getLastDayOfMonth(year, month) : 31;

        return {
            isLiveData: false,
            options: this.getDayFormSelectOptions(lastDayOfMonth)
        };
    }

    /*
     ** We request the last day of a month by providing 0 as the day and the next month as the month.
     ** Because javascript months are 0 based we already have accounted for selecting the next month.
     */
    private getLastDayOfMonth(year: number, monthCountingFromOne: number): number {
        return new Date(year, monthCountingFromOne, 0).getDate();
    }

    private getDayFormSelectOptions(lengthOfMonth: number): FormSelectOption[] {
        return Array.from({ length: lengthOfMonth }, (x, i) => {
            const value = `${i + 1}`;
            return { label: value, value };
        });
    }

    private initFormListener(): void {
        this.synchronizeInputControlValues(this.internalYearControl, this.yearControl);
        this.synchronizeMultiSelectControlValues(this.internalMonthControl, this.monthControl);
        this.synchronizeMultiSelectControlValues(this.internalDayControl, this.dayControl);
    }

    private synchronizeInputControlValues(
        internalControl: UntypedFormControl,
        externalControl: UntypedFormControl
    ): void {
        externalControl.valueChanges
            .pipe(startWith(externalControl.value), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(value => internalControl.setValue(value ? value : null));

        internalControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(value => externalControl.setValue(value ? value : null));

        externalControl.statusChanges
            .pipe(startWith(externalControl.status), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(status => {
                if (status === 'DISABLED') {
                    internalControl.disable();
                    return;
                }

                internalControl.enable();
                internalControl.setErrors(externalControl.errors);
            });
    }

    private synchronizeMultiSelectControlValues(
        internalControl: UntypedFormControl,
        externalControl: UntypedFormControl
    ): void {
        externalControl.valueChanges
            .pipe(startWith(externalControl.value), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(value => internalControl.setValue(value ? [`${value}`] : []));

        internalControl.valueChanges
            .pipe(
                map(values => (values && values[0]) || null),
                map(value => (value ? Number.parseInt(value, 10) : null)),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(value => externalControl.setValue(value));

        externalControl.statusChanges
            .pipe(startWith(externalControl.status), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(status => {
                if (status === 'DISABLED') {
                    internalControl.disable();
                    return;
                }

                internalControl.enable();
                internalControl.setErrors(externalControl.errors);
            });
    }

    private getMonthMultiFormSelectOptions(): FormSelectOption[] {
        return Array.from({ length: 12 }, (x, i) => {
            const value = `${i + 1}`;
            const label: string = new Date(2020, i, 1).toLocaleDateString(this.localeId, { month: 'long' });
            return { label, value };
        });
    }

    get yearControl(): UntypedFormControl {
        return this.control.get('Year') as UntypedFormControl;
    }

    get monthControl(): UntypedFormControl {
        return this.control.get('Month') as UntypedFormControl;
    }

    get dayControl(): UntypedFormControl {
        return this.control.get('Day') as UntypedFormControl;
    }
}
